/*
 Copyright 2017-2021 Amazon.com, Inc. or its affiliates. All Rights Reserved.

 Licensed under the Amazon Software License (the "License"). You may not use this file
 except in compliance with the License. A copy of the License is located at

 http://aws.amazon.com/asl/

 or in the "license" file accompanying this file. This file is distributed on an "AS IS"
 BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. See the
 License for the specific language governing permissions and limitations under the License.
 */

/**
 * Vuex store recorder handlers
 */

/* eslint no-console: ["error", { allow: ["info", "warn", "error", "time", "timeEnd"] }] */
/* eslint no-param-reassign: ["error", { "props": false }] */

import {liveChatStatus} from "./state";

export const createLiveChatSession = result =>
  (window.connect.ChatSession.create({
    chatDetails: result.startChatResult,
    type: 'CUSTOMER',
  }));

export const connectLiveChatSession = session =>
  Promise.resolve(session.connect().then((response) => {
    void response;
    return Promise.resolve(response);
  }, (error) => {
    return Promise.reject(error);
  }));

function recordSessionAttributes(context, chatDetails) {
  if (chatDetails && chatDetails.initialContactId) {
    context.commit("setLexSessionAttributeValue", { key: 'connect_initial_contact_id', value: chatDetails.initialContactId });
  }
  if (chatDetails && chatDetails.contactId) {
    context.commit("setLexSessionAttributeValue", { key: 'connect_contact_id', value: chatDetails.contactId });
  }
  if (chatDetails && chatDetails.participantId) {
    context.commit("setLexSessionAttributeValue", { key: 'connect_participant_id', value: chatDetails.participantId });
  }
}

export const initLiveChatHandlers = (context, session) => {
  session.onConnectionEstablished((data) => {
    if (data && data.chatDetails) {
      recordSessionAttributes(context, data.chatDetails);
    }
    // context.dispatch('pushLiveChatMessage', {
    //   type: 'agent',
    //   text: 'Live Chat Connection Established',
    // });
  });

  session.onMessage((event) => {
    const { chatDetails, data } = event;
    if (chatDetails) {
      recordSessionAttributes(context, chatDetails);
    }
    let type = '';
    switch (data.ContentType) {
      case 'application/vnd.amazonaws.connect.event.participant.joined':
        switch (data.ParticipantRole) {
          case 'SYSTEM':
            context.commit('setIsLiveChatProcessing', false);
            break;
          case 'AGENT':
            context.dispatch('liveChatAgentJoined');
            context.commit('setIsLiveChatProcessing', false);
            {
              const joinedTemplate = context.state.config.connect.agentJoinedMessage || '{Agent} has joined the chat.';
              const agentDisplayName = data.DisplayName || 'Agent';
            context.dispatch('pushLiveChatMessage', {
              type: 'agent',
              text: joinedTemplate.replaceAll("{Agent}", agentDisplayName),
            });
            }

            const transcriptArray = context.getters.liveChatTextTranscriptArray();
            transcriptArray.forEach((text, index) => {
              var formattedText = "Bot Transcript: (" + (index + 1).toString() + "\\" + transcriptArray.length + ")\n" + text;
              const transcriptDelayInMsec = context.state.config.connect.transcriptMessageDelayInMsec || 150;
              sendChatMessageWithDelay(session, formattedText, index * transcriptDelayInMsec);
            });

            if(context.state.config.connect.attachChatTranscript &&
              (context.state.config.connect.attachChatTranscript === 'true'
                || context.state.config.connect.attachChatTranscript === true )
            ) {
              var textFile = context.getters.liveChatTranscriptFile();
              session.controller.sendAttachment({
                attachment: textFile
              }).then(response => {
              }, reason => {
              });
            }
            break;
          case 'CUSTOMER':
            break;
          default:
            break;
        }
        break;
      case 'application/vnd.amazonaws.connect.event.participant.left':
        switch (data.ParticipantRole) {
          case 'SYSTEM':
            break;
          case 'AGENT':
            {
              const leftTemplate = context.state.config.connect.agentLeftMessage || '{Agent} has left.';
              const agentDisplayName = data.DisplayName || 'Agent';
            context.dispatch('pushLiveChatMessage', {
              type: 'agent',
              text: leftTemplate.replaceAll("{Agent}", agentDisplayName),
            });
            }
            break;
          case 'CUSTOMER':
            break;
          default:
            break;
        }
        break;
      case 'application/vnd.amazonaws.connect.event.chat.ended':
        if (context.state.liveChat.status !== liveChatStatus.ENDED) {
          const chatEndedMessage = context.state.config.connect.chatEndedMessage || 'The chat session has ended.';
          context.dispatch('pushLiveChatMessage', {
            type: 'agent',
            text: chatEndedMessage,
          });
          context.dispatch('liveChatSessionEnded');
        }
        break;
      case 'text/plain':
        switch (data.ParticipantRole) {
          case 'SYSTEM':
            type = 'bot';
            break;
          case 'AGENT':
            type = 'agent';
            break;
          case 'CUSTOMER':
            type = 'human';
            break;
          default:
            break;
        }
        context.commit('setIsLiveChatProcessing', false);
        if(!data.Content.startsWith('Bot Transcript')) {
          context.dispatch('pushLiveChatMessage', {
            type,
            text: data.Content,
          });
        }
        break;
      default:
        break;
    }
  });

  session.onTyping((typingEvent) => {
    if (typingEvent.data.ParticipantRole === 'AGENT') {
      context.dispatch('agentIsTyping');
    }
  });

  session.onConnectionBroken((data) => {
    context.dispatch('liveChatSessionReconnectRequest');
  });

  /*
  NOT WORKING
  session.onEnded((data) => {
    context.dispatch('liveChatSessionEnded');
  });
  */
};

export const sendChatMessage = async (liveChatSession, message) => {
  await liveChatSession.controller.sendMessage({
    message,
    contentType: 'text/plain',
  });
};

export const sendChatMessageWithDelay = async (liveChatSession, message, delay) => {
  setTimeout(async () => {
    await liveChatSession.controller.sendMessage({
      message,
      contentType: 'text/plain',
    });
  }, delay);
};

export const sendTypingEvent = (liveChatSession) => {
  liveChatSession.controller.sendEvent({
    contentType: 'application/vnd.amazonaws.connect.event.typing',
  });
};

export const requestLiveChatEnd = (liveChatSession) => {
  liveChatSession.controller.disconnectParticipant();
};
